
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getTemplates, deleteTemplate } from "@/services/templateService";
import { Template } from "@/types/whatsApp";
import { Edit, Trash2, RefreshCw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

export const TemplatesList = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this template?")) {
      try {
        await deleteTemplate(id);
        setTemplates(templates.filter(t => t.id !== id));
        toast({
          title: "Success",
          description: "Template deleted successfully",
        });
      } catch (error) {
        console.error('Error deleting template:', error);
        toast({
          title: "Error",
          description: "Failed to delete template",
          variant: "destructive",
        });
      }
    }
  };

  if (isLoading && templates.length === 0) {
    return <div className="flex justify-center p-6"><RefreshCw className="animate-spin h-6 w-6" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-xl font-bold">Message Templates</h2>
        <Button size="sm" variant="outline" onClick={fetchTemplates} disabled={isLoading}>
          {isLoading ? <RefreshCw className="animate-spin h-4 w-4 mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-gray-400">No templates found. Create your first template to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-medium text-lg mb-1">{template.name}</h3>
                  <p className="text-sm text-gray-400">
                    Variables: {template.variables.join(", ")}
                  </p>
                </div>
                <div className="p-4 bg-gray-900/50">
                  <p className="text-sm mb-4 text-gray-300 line-clamp-3">{template.content}</p>
                  <div className="flex justify-between">
                    <Button variant="outline" size="sm">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(template.id)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
